import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { StepUpload } from "@/components/workspace/StepUpload";
import { StepCheck } from "@/components/workspace/StepCheck";
import { StepBlueprint } from "@/components/workspace/StepBlueprint";
import { StepEvaluate } from "@/components/workspace/StepEvaluate";
import { ChatDock } from "@/components/workspace/ChatDock";
import { WhatsAppExpertCTA } from "@/components/WhatsAppExpertCTA";
import { ArrowLeft, UploadCloud, ScanSearch, Map, ClipboardCheck, Share2 } from "lucide-react";

const STEPS = [
  { key: "upload", label: "Upload", icon: UploadCloud },
  { key: "check", label: "Check", icon: ScanSearch },
  { key: "blueprint", label: "Blueprint", icon: Map },
  { key: "evaluate", label: "Evaluate", icon: ClipboardCheck },
];

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [step, setStep] = useState("upload");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assignments/${id}`);
      setAssignment(data);
    } catch (e) { navigate("/app"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleShare = async () => {
    const { data } = await api.patch(`/assignments/${id}/share`);
    setAssignment((a) => ({ ...a, shared_with_tutor: data.shared_with_tutor }));
  };

  if (!assignment) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64 rounded-lg" /><Skeleton className="h-96 w-full rounded-2xl" /></div>;
  }

  const stepDone = {
    upload: !!assignment.brief_text,
    check: !!assignment.confirmed_requirements,
    blueprint: !!assignment.blueprint,
    evaluate: !!assignment.has_evaluation,
  };

  return (
    <div className="space-y-6" data-testid="workspace">
      <button onClick={() => navigate("/app")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-dashboard">
        <ArrowLeft className="h-4 w-4" /> All assignments
      </button>

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{assignment.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{assignment.study_country} · Next: {assignment.next_action}</p>
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm">
          <Share2 className="h-4 w-4 text-muted-foreground" /> Share with tutor
          <Switch checked={assignment.shared_with_tutor} onCheckedChange={toggleShare} data-testid="share-tutor-toggle" />
        </label>
      </div>

      <WhatsAppExpertCTA assignmentTitle={assignment.title} />

      {/* step indicator */}
      <div className="flex items-center gap-1 overflow-x-auto rounded-2xl border border-border bg-card p-1.5">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(s.key)} data-testid={`step-${s.key}`}
            className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${step === s.key ? "bg-[#0B5D3B] text-white dark:bg-[#25D366] dark:text-black" : "text-muted-foreground hover:bg-accent"}`}>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${step === s.key ? "bg-white/25" : stepDone[s.key] ? "bg-[#059669] text-white" : "bg-muted"}`}>
              {stepDone[s.key] ? "✓" : i + 1}
            </span>
            {s.label}
          </button>
        ))}
      </div>

      <div className="fade-up">
        {step === "upload" && <StepUpload assignment={assignment} reload={load} />}
        {step === "check" && <StepCheck key={assignment.parsed ? "p" : "np"} assignment={assignment} reload={load} />}
        {step === "blueprint" && <StepBlueprint assignment={assignment} reload={load} />}
        {step === "evaluate" && <StepEvaluate assignment={assignment} reload={load} />}
      </div>

      <ChatDock assignmentId={assignment.id} />
    </div>
  );
}
